import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { Calendar, Clock, Building2, BarChart3, Users } from 'lucide-react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import dashboardService, {
  DueDatesData,
  StatusCountsData,
  InspectionTypeCountsData,
  TradeCountsData,
  AverageTurnAroundTimeItem,
} from '../services/dashboardService';
import { projectService } from '../services/projectService';
import { Project } from '../types/project';
import api from '../services/api';
import '../styles/Buttons.css';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface InspectionType {
  _id: string;
  name: string;
}

const ServiceDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();

  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');

  // Chart data states
  const [dueDates, setDueDates] = useState<DueDatesData>({ thisWeek: 0, nextWeek: 0, overdue: 0 });
  const [averageTurnAroundTime, setAverageTurnAroundTime] = useState<AverageTurnAroundTimeItem[]>([]);
  const [statusCounts, setStatusCounts] = useState<StatusCountsData>({});
  const [inspectionTypeCounts, setInspectionTypeCounts] = useState<InspectionTypeCountsData>({});
  const [tradeCounts, setTradeCounts] = useState<TradeCountsData>({});

  // Loading states for each chart
  const [loadingDueDates, setLoadingDueDates] = useState(false);
  const [loadingTurnAround, setLoadingTurnAround] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [loadingInspectionType, setLoadingInspectionType] = useState(false);
  const [loadingTrade, setLoadingTrade] = useState(false);

  // Filter states
  const [statusChartInspectionTypeFilter, setStatusChartInspectionTypeFilter] = useState<string | null>(null);
  const [inspectionTypeChartStatusFilter, setInspectionTypeChartStatusFilter] = useState<string | null>(null);
  const [tradeChartInspectionTypeFilter, setTradeChartInspectionTypeFilter] = useState<string | null>(null);

  // Inspection types for dropdown
  const [inspectionTypes, setInspectionTypes] = useState<InspectionType[]>([]);
  const [inspectionTypeNameToId, setInspectionTypeNameToId] = useState<{ [name: string]: string }>({});

  useEffect(() => {
    loadProjects();
  }, [workspaceId]);

  useEffect(() => {
    if (selectedProjectId) {
      loadInspectionTypes();
      loadAllDashboardData();
    }
  }, [selectedProjectId]);

  const loadProjects = async () => {
    if (!workspaceId) return;

    try {
      setLoading(true);
      const response = await projectService.getAll(workspaceId);
      setProjects(response.projects || []);

      // Auto-select first project
      if (response.projects.length > 0 && !selectedProjectId) {
        setSelectedProjectId(response.projects[0]._id);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadInspectionTypes = async () => {
    if (!selectedProjectId) return;

    try {
      const response = await api.get(`/service-settings/${selectedProjectId}/inspection-types`);
      const types = response.data.items || [];
      setInspectionTypes(types);

      // Create name-to-ID mapping
      const mapping: { [name: string]: string } = {};
      types.forEach((type: InspectionType) => {
        mapping[type.name] = type._id;
      });
      setInspectionTypeNameToId(mapping);
    } catch (error) {
      console.error('Error loading inspection types:', error);
    }
  };

  const loadAllDashboardData = async () => {
    if (!workspaceId || !selectedProjectId) return;

    await Promise.all([
      loadDueDates(),
      loadAverageTurnAroundTime(),
      loadStatusCounts(),
      loadInspectionTypeCounts(),
      loadTradeCounts(),
    ]);
  };

  const loadDueDates = async () => {
    if (!workspaceId || !selectedProjectId) return;

    try {
      setLoadingDueDates(true);
      const data = await dashboardService.getDueDates(workspaceId, selectedProjectId);
      setDueDates(data);
    } catch (error) {
      console.error('Error loading due dates:', error);
    } finally {
      setLoadingDueDates(false);
    }
  };

  const loadAverageTurnAroundTime = async () => {
    if (!workspaceId || !selectedProjectId) return;

    try {
      setLoadingTurnAround(true);
      const data = await dashboardService.getAverageTurnAroundTime(workspaceId, selectedProjectId);
      setAverageTurnAroundTime(data);
    } catch (error) {
      console.error('Error loading average turn around time:', error);
    } finally {
      setLoadingTurnAround(false);
    }
  };

  const loadStatusCounts = async () => {
    if (!workspaceId || !selectedProjectId) return;

    try {
      setLoadingStatus(true);

      // Convert inspection type name to ID if filter is set
      const inspectionTypeId = statusChartInspectionTypeFilter
        ? inspectionTypeNameToId[statusChartInspectionTypeFilter]
        : undefined;

      const data = await dashboardService.getDeficienciesByStatus(
        workspaceId,
        selectedProjectId,
        inspectionTypeId
      );
      setStatusCounts(data);
    } catch (error) {
      console.error('Error loading status counts:', error);
    } finally {
      setLoadingStatus(false);
    }
  };

  const loadInspectionTypeCounts = async () => {
    if (!workspaceId || !selectedProjectId) return;

    try {
      setLoadingInspectionType(true);
      const data = await dashboardService.getDeficienciesByInspectionType(
        workspaceId,
        selectedProjectId,
        inspectionTypeChartStatusFilter || undefined
      );
      setInspectionTypeCounts(data);
    } catch (error) {
      console.error('Error loading inspection type counts:', error);
    } finally {
      setLoadingInspectionType(false);
    }
  };

  const loadTradeCounts = async () => {
    if (!workspaceId || !selectedProjectId) return;

    try {
      setLoadingTrade(true);

      // Convert inspection type name to ID if filter is set
      const inspectionTypeId = tradeChartInspectionTypeFilter
        ? inspectionTypeNameToId[tradeChartInspectionTypeFilter]
        : undefined;

      const data = await dashboardService.getDeficienciesByTrade(
        workspaceId,
        selectedProjectId,
        inspectionTypeId
      );
      setTradeCounts(data);
    } catch (error) {
      console.error('Error loading trade counts:', error);
    } finally {
      setLoadingTrade(false);
    }
  };

  const handleBackToWorkspaces = () => {
    navigate('/workspaces');
  };

  const handleProjectChange = (projectId: string) => {
    setSelectedProjectId(projectId);
    // Reset filters when project changes
    setStatusChartInspectionTypeFilter(null);
    setInspectionTypeChartStatusFilter(null);
    setTradeChartInspectionTypeFilter(null);
  };

  // Chart options for status counts
  const statusChartData = {
    labels: Object.keys(statusCounts),
    datasets: [
      {
        label: 'Deficiencies',
        data: Object.values(statusCounts),
        backgroundColor: '#48182F',
        borderRadius: 6,
        barPercentage: 0.6,
        categoryPercentage: 0.7,
      },
    ],
  };

  const commonChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(72, 24, 47, 0.95)',
        padding: 12,
        titleFont: {
          size: 13,
          weight: 'bold' as const,
        },
        bodyFont: {
          size: 12,
        },
        borderColor: 'rgba(72, 24, 47, 0.2)',
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 11,
          },
          color: '#6B7280',
        },
        border: {
          color: 'rgba(212, 203, 196, 0.5)',
          width: 1.5,
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(212, 203, 196, 0.2)',
          lineWidth: 0.5,
        },
        ticks: {
          precision: 0,
          font: {
            size: 11,
          },
          color: '#6B7280',
        },
        border: {
          color: 'rgba(212, 203, 196, 0.5)',
          width: 1.5,
        },
      },
    },
  };

  // Chart options for inspection type counts
  const inspectionTypeChartData = {
    labels: Object.keys(inspectionTypeCounts),
    datasets: [
      {
        label: 'Deficiencies',
        data: Object.values(inspectionTypeCounts),
        backgroundColor: '#6366F1',
        borderRadius: 6,
        barPercentage: 0.6,
        categoryPercentage: 0.7,
      },
    ],
  };

  // Chart options for trade counts
  const tradeChartData = {
    labels: Object.keys(tradeCounts),
    datasets: [
      {
        label: 'Deficiencies',
        data: Object.values(tradeCounts),
        backgroundColor: '#10B981',
        borderRadius: 6,
        barPercentage: 0.6,
        categoryPercentage: 0.7,
      },
    ],
  };

  // Chart options for average turn around time
  const turnAroundTimeChartData = {
    labels: averageTurnAroundTime.map(item => item.inspectionTypeName),
    datasets: [
      {
        label: 'Average Days',
        data: averageTurnAroundTime.map(item => item.averageDays),
        backgroundColor: '#F59E0B',
        borderRadius: 6,
        barPercentage: 0.6,
        categoryPercentage: 0.7,
      },
    ],
  };

  return (
    <AppLayout onBack={handleBackToWorkspaces}>
      <div className="page-container">
        <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ margin: 0 }}>Service Dashboard</h1>
            <p style={{ fontSize: '14px', color: '#6B7280', margin: '8px 0 0 0' }}>
              Overview of all service activities
            </p>
          </div>

          {/* Project Selector */}
          {projects.length > 0 && (
            <div style={{ minWidth: '250px' }}>
              <select
                value={selectedProjectId}
                onChange={(e) => handleProjectChange(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#48182F',
                  backgroundColor: 'white',
                  border: '1.5px solid #D4CBC4',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  outline: 'none',
                  boxShadow: '0 1px 3px rgba(72, 24, 47, 0.08)',
                  transition: 'all 0.2s',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#48182F';
                  e.target.style.boxShadow = '0 0 0 3px rgba(72, 24, 47, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#D4CBC4';
                  e.target.style.boxShadow = '0 1px 3px rgba(72, 24, 47, 0.08)';
                }}
              >
                {projects.map((project) => (
                  <option key={project._id} value={project._id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="page-content" style={{ marginTop: '24px' }}>
          {loading ? (
            <div className="builders-loading">
              <div className="builders-loading-spinner" />
            </div>
          ) : !selectedProjectId ? (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '48px',
              textAlign: 'center',
              color: '#6B7280',
              boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
            }}>
              <Building2 size={48} style={{ color: '#D4CBC4', marginBottom: '16px' }} />
              <p style={{ fontSize: '16px', margin: 0 }}>No projects available. Please create a project first.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* Due Dates Overview */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px', gap: '12px' }}>
                  <div style={{
                    padding: '8px',
                    backgroundColor: 'rgba(72, 24, 47, 0.1)',
                    borderRadius: '8px',
                  }}>
                    <Calendar size={20} color="#48182F" />
                  </div>
                  <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0, color: '#48182F' }}>
                    Due Dates Overview
                  </h2>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '16px'
                }}>
                  <DueDateCard
                    title="Due This Week"
                    count={dueDates.thisWeek}
                    color="#3B82F6"
                    loading={loadingDueDates}
                  />
                  <DueDateCard
                    title="Due Next Week"
                    count={dueDates.nextWeek}
                    color="#F59E0B"
                    loading={loadingDueDates}
                  />
                  <DueDateCard
                    title="Over Due Deficiency"
                    count={dueDates.overdue}
                    color="#EF4444"
                    loading={loadingDueDates}
                  />
                </div>
              </div>

              {/* Average Turn Around Time */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px', gap: '12px' }}>
                  <div style={{
                    padding: '8px',
                    backgroundColor: 'rgba(245, 158, 11, 0.1)',
                    borderRadius: '8px',
                  }}>
                    <Clock size={20} color="#F59E0B" />
                  </div>
                  <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0, color: '#48182F' }}>
                    Avg Turn Around Time / Inspection Type
                  </h2>
                </div>

                {loadingTurnAround ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                    <div className="builders-loading-spinner" style={{ margin: '0 auto' }} />
                  </div>
                ) : averageTurnAroundTime.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                    <p>No turn around time data available</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {averageTurnAroundTime.map((item, index) => {
                      const days = Math.floor(item.averageDays);
                      const hours = Math.floor((item.averageDays - days) * 24);
                      const mins = Math.floor(((item.averageDays - days) * 24 - hours) * 60);

                      return (
                        <div key={index} style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                          <div style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: '#48182F',
                            flexShrink: 0,
                            marginTop: '6px'
                          }} />
                          <div style={{ flex: 1 }}>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              marginBottom: '8px'
                            }}>
                              <div>
                                <div style={{
                                  fontSize: '14px',
                                  fontWeight: '600',
                                  color: '#48182F',
                                  marginBottom: '4px'
                                }}>
                                  {item.inspectionTypeName}
                                </div>
                                <div style={{
                                  fontSize: '12px',
                                  color: '#6B7280'
                                }}>
                                  {item.count} deficiencies
                                </div>
                              </div>
                              <div style={{
                                backgroundColor: '#F3F4F6',
                                padding: '6px 12px',
                                borderRadius: '6px',
                                fontSize: '13px',
                                fontWeight: '500',
                                color: '#4B5563',
                                whiteSpace: 'nowrap'
                              }}>
                                {days} days {hours} hours {mins} mins
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Deficiencies by Status */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '20px',
                  flexWrap: 'wrap',
                  gap: '12px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      padding: '8px',
                      backgroundColor: 'rgba(72, 24, 47, 0.1)',
                      borderRadius: '8px',
                    }}>
                      <BarChart3 size={20} color="#48182F" />
                    </div>
                    <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0, color: '#48182F' }}>
                      Deficiencies by Status
                    </h2>
                  </div>

                  <select
                    value={statusChartInspectionTypeFilter || 'all'}
                    onChange={(e) => {
                      setStatusChartInspectionTypeFilter(e.target.value === 'all' ? null : e.target.value);
                      setTimeout(loadStatusCounts, 0);
                    }}
                    disabled={loadingStatus}
                    style={{
                      padding: '8px 12px',
                      fontSize: '13px',
                      fontWeight: '500',
                      color: statusChartInspectionTypeFilter ? '#48182F' : '#6B7280',
                      backgroundColor: loadingStatus ? 'rgba(245, 243, 240, 0.5)' : '#F5F3F0',
                      border: '1px solid rgba(212, 203, 196, 0.5)',
                      borderRadius: '8px',
                      cursor: loadingStatus ? 'not-allowed' : 'pointer',
                      outline: 'none',
                    }}
                  >
                    <option value="all">All Inspection Types</option>
                    {inspectionTypes.map((type) => (
                      <option key={type._id} value={type.name}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>

                {loadingStatus ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                    <div className="builders-loading-spinner" style={{ margin: '0 auto' }} />
                  </div>
                ) : Object.keys(statusCounts).length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                    <p>No status data available</p>
                  </div>
                ) : (
                  <div style={{ height: '300px' }}>
                    <Bar data={statusChartData} options={commonChartOptions} />
                  </div>
                )}
              </div>

              {/* Deficiencies by Inspection Type */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '20px',
                  flexWrap: 'wrap',
                  gap: '12px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      padding: '8px',
                      backgroundColor: 'rgba(99, 102, 241, 0.1)',
                      borderRadius: '8px',
                    }}>
                      <BarChart3 size={20} color="#6366F1" />
                    </div>
                    <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0, color: '#48182F' }}>
                      Deficiencies by Inspection Type
                    </h2>
                  </div>

                  <select
                    value={inspectionTypeChartStatusFilter || 'all'}
                    onChange={(e) => {
                      setInspectionTypeChartStatusFilter(e.target.value === 'all' ? null : e.target.value);
                      setTimeout(loadInspectionTypeCounts, 0);
                    }}
                    disabled={loadingInspectionType}
                    style={{
                      padding: '8px 12px',
                      fontSize: '13px',
                      fontWeight: '500',
                      color: inspectionTypeChartStatusFilter ? '#48182F' : '#6B7280',
                      backgroundColor: loadingInspectionType ? 'rgba(245, 243, 240, 0.5)' : '#F5F3F0',
                      border: '1px solid rgba(212, 203, 196, 0.5)',
                      borderRadius: '8px',
                      cursor: loadingInspectionType ? 'not-allowed' : 'pointer',
                      outline: 'none',
                    }}
                  >
                    <option value="all">All Statuses</option>
                    <option value="Open">Open</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Closed">Closed</option>
                    <option value="Resolved">Resolved</option>
                  </select>
                </div>

                {loadingInspectionType ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                    <div className="builders-loading-spinner" style={{ margin: '0 auto' }} />
                  </div>
                ) : Object.keys(inspectionTypeCounts).length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                    <p>No inspection type data available</p>
                  </div>
                ) : (
                  <div style={{ height: '300px' }}>
                    <Bar data={inspectionTypeChartData} options={commonChartOptions} />
                  </div>
                )}
              </div>

              {/* Trade Wise Data */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '20px',
                  flexWrap: 'wrap',
                  gap: '12px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      padding: '8px',
                      backgroundColor: 'rgba(16, 185, 129, 0.1)',
                      borderRadius: '8px',
                    }}>
                      <Users size={20} color="#10B981" />
                    </div>
                    <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0, color: '#48182F' }}>
                      Trade Wise Data
                    </h2>
                  </div>

                  <select
                    value={tradeChartInspectionTypeFilter || 'all'}
                    onChange={(e) => {
                      setTradeChartInspectionTypeFilter(e.target.value === 'all' ? null : e.target.value);
                      setTimeout(loadTradeCounts, 0);
                    }}
                    disabled={loadingTrade}
                    style={{
                      padding: '8px 12px',
                      fontSize: '13px',
                      fontWeight: '500',
                      color: tradeChartInspectionTypeFilter ? '#48182F' : '#6B7280',
                      backgroundColor: loadingTrade ? 'rgba(245, 243, 240, 0.5)' : '#F5F3F0',
                      border: '1px solid rgba(212, 203, 196, 0.5)',
                      borderRadius: '8px',
                      cursor: loadingTrade ? 'not-allowed' : 'pointer',
                      outline: 'none',
                    }}
                  >
                    <option value="all">All Inspection Types</option>
                    {inspectionTypes.map((type) => (
                      <option key={type._id} value={type.name}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>

                {loadingTrade ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                    <div className="builders-loading-spinner" style={{ margin: '0 auto' }} />
                  </div>
                ) : Object.keys(tradeCounts).length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                    <p>No trade data available</p>
                  </div>
                ) : (
                  <div style={{ height: '300px' }}>
                    <Bar data={tradeChartData} options={commonChartOptions} />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

// Due Date Card Component
interface DueDateCardProps {
  title: string;
  count: number;
  color: string;
  loading: boolean;
}

const DueDateCard: React.FC<DueDateCardProps> = ({ title, count, color, loading }) => {
  return (
    <div style={{
      backgroundColor: '#F5F3F0',
      border: `1.5px solid ${color}33`,
      borderRadius: '12px',
      padding: '20px',
      boxShadow: `0 2px 8px ${color}15`,
    }}>
      <p style={{
        fontSize: '13px',
        fontWeight: '600',
        color: '#6B7280',
        margin: '0 0 12px 0',
        lineHeight: 1.3
      }}>
        {title}
      </p>
      {loading ? (
        <div style={{ textAlign: 'center', padding: '10px' }}>
          <div className="builders-loading-spinner" style={{ width: '24px', height: '24px', margin: '0 auto' }} />
        </div>
      ) : (
        <p style={{
          fontSize: '32px',
          fontWeight: '700',
          color: color,
          margin: 0
        }}>
          {count}
        </p>
      )}
    </div>
  );
};

export default ServiceDashboard;
